import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import { Children, cloneElement, useEffect, useState } from 'react';
import useMounted from 'react-utils/hooks/useMounted';
import styled from 'foundations-theming/utils';
import { OuterWrapper } from '../Alert/Alert';
import { FLOATING_ALERT_TIMING } from '../../../constants/times';
import { jsx as _jsx } from "react/jsx-runtime";
const transitionTime = parseInt(FLOATING_ALERT_TIMING, 10);
const PopTransitionWrapper = styled.div.withConfig({
  displayName: "AlertListTransition__PopTransitionWrapper",
  componentId: "sc-1lbpurl-0"
})(({
  open
}) => {
  return {
    opacity: open ? 1 : 0,
    transform: open ? 'scale(1)' : 'scale(0.9)',
    transitionProperty: 'opacity, transform',
    transitionDuration: `${transitionTime}ms`,
    pointerEvents: 'none',
    [`& ${OuterWrapper}`]: {
      pointerEvents: 'auto'
    }
  };
});
const renderWrappedChild = (child, open) => /*#__PURE__*/_jsx(PopTransitionWrapper, {
  "data-test-id": "pop-transition-wrapper",
  open: open,
  children: child
}, child.key);
const PopTransition = ({
  children
}) => {
  const [visibleChildren, setVisibleChildren] = useState([]);
  const [leavingChildrenKeys, setLeavingChildrenKeys] = useState({});
  const mountedRef = useMounted();

  // Child add/remove logic
  useEffect(() => {
    const visibleChildrenKeys = visibleChildren.reduce((acc, visibleChild) => {
      if (visibleChild.key !== null) {
        acc[visibleChild.key] = true;
      }
      return acc;
    }, {});
    const removedChildrenKeys = Object.assign({}, visibleChildrenKeys);

    // Merge our existing set of visible children with any new children
    Children.forEach(children, child => {
      const childKey = child && child.key;
      if (!childKey) return;
      if (!visibleChildrenKeys[childKey]) {
        // A new child has been added
        setVisibleChildren(prevVisibleChildren => prevVisibleChildren.concat(renderWrappedChild(child, false)));

        // Start "enter" animation
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!mountedRef.current) return;
            setVisibleChildren(prevVisibleChildren => prevVisibleChildren.map(c => c.key === childKey ? /*#__PURE__*/cloneElement(c, {
              open: true
            }) : c));
          });
        });
      }
      delete removedChildrenKeys[childKey];
    });

    // Handle removed children
    Object.keys(removedChildrenKeys).forEach(removedKey => {
      if (leavingChildrenKeys[removedKey]) return;
      setLeavingChildrenKeys(prevLeavingChildrenKeys => Object.assign({}, prevLeavingChildrenKeys, {
        [removedKey]: true
      }));

      // Start "leave" animation, then remove the item completely afterward.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!mountedRef.current) return;
          setVisibleChildren(prevVisibleChildren => prevVisibleChildren.map(c => c.key === removedKey ? /*#__PURE__*/cloneElement(c, {
            open: false
          }) : c));
          setTimeout(() => {
            if (!mountedRef.current) return;
            setVisibleChildren(prevVisibleChildren => prevVisibleChildren.filter(c => c.key !== removedKey));
            setLeavingChildrenKeys(prevLeavingChildrenKeys => Object.assign({}, prevLeavingChildrenKeys, {
              [removedKey]: false
            }));
          }, transitionTime);
        });
      });
    });
  }, [children]); // eslint-disable-line react-hooks/exhaustive-deps

  return visibleChildren;
};
export const AlertListTransition = _ref => {
  let props = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
  return /*#__PURE__*/_jsx(PopTransition, Object.assign({}, props));
};